/* eslint-disable no-useless-escape */
/* eslint-disable no-undef */
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { processError } from './errorProcessor';
import { logout, refreshConnection } from '../store/actions/auth';
import { store } from '../store/store';

interface AuthState {
  access_token: string;
  current_employee: string;
}

interface RootState {
  auth: AuthState;
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const { dispatch } = store;

const apiService: AxiosInstance = axios.create({
  xsrfHeaderName: 'X-CSRFTOKEN',
  xsrfCookieName: 'csrftoken',
  baseURL: import.meta.env.DEV ? '' : import.meta.env.VITE_BASE_API,
  withCredentials: true,
  withXSRFToken: true,
});
apiService.defaults.params = {};

const updateToken = (): void => {
  apiService.defaults.headers.common['Authorization'] = `Bearer ${
    store.getState().auth.access_token
  }`;
};

let csrfInFlight: Promise<void> | null = null;

const ensureCsrfToken = (): Promise<void> => {
  if (csrfInFlight) {
    return csrfInFlight;
  }

  csrfInFlight = apiService
    .get('/my-rest-auth/csrf/', { silentError: true })
    .then(() => undefined)
    .finally(() => {
      csrfInFlight = null;
    });

  return csrfInFlight;
};

let refreshInFlight: Promise<string> | null = null;

const refreshAccessToken = (): Promise<string> => {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = apiService
    .post('/my-rest-auth/token/refresh/')
    .then(({ data }) => {
      const access = data?.access as string | undefined;
      if (!access) {
        throw new Error('No access token returned from refresh');
      }
      dispatch(refreshConnection(access));
      updateToken();
      return access;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

apiService.defaults.headers.common['Content-Type'] = 'application/json';
// updateToken();
apiService.defaults.headers.common['Authorization'] = `Bearer ${
  store.getState().auth.access_token
}`;

apiService.interceptors.request.use(
  function (config: InternalAxiosRequestConfig) {
    if (config.url === '/my-rest-auth/login/' || config.url === '/my-rest-auth/google-login/') {
      config.headers.delete('Authorization');
    }
    const lang = navigator.language;
    if (lang) {
      config.params['lang'] = lang;
    }

    return config;
  },
  function (error: AxiosError) {
    return Promise.reject(error);
  }
);

apiService.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (
    !config.url?.match(/(.*)[\/](login)[[\/](.*)/g) &&
    config.url !== '/my-rest-auth/google-login/'
  ) {
    const employee = (store.getState() as RootState).auth.current_employee;

    config.params = config.params || {};
    config.params['employee'] = employee;
    config.headers['X-Employee-ID'] = employee;
  }
  return config;
});

apiService.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalConfig = error.config as RetryableRequestConfig | undefined;
    const silent = originalConfig?.silentError === true;

    // Síťová chyba / bez odpovědi serveru
    if (typeof error.response === 'undefined') {
      if (!silent) {
        toast.error('Chyba', { description: 'Zkontrolujte zda jste připojeni k internetu' });
      }
      return Promise.reject(error);
    }

    const status = error.response.status;

    // 401 → tichý pokus o refresh tokenu; toast až když refresh není možný / selhal
    if (
      status === 401 &&
      originalConfig &&
      originalConfig.url !== '/my-rest-auth/login/' &&
      !originalConfig._retry
    ) {
      originalConfig._retry = true;

      // Selhal samotný refresh endpoint → tvrdý logout
      if (originalConfig.url === '/my-rest-auth/token/refresh/') {
        dispatch(logout());
        return Promise.reject(error);
      }

      try {
        const access = await refreshAccessToken();
        originalConfig.headers.Authorization = `Bearer ${access}`;
        return apiService(originalConfig); // úspěšný refresh → žádný toast
      } catch (_e) {
        // propadne do standardního zpracování níže
      }

      // Refresh nebyl možný / selhal → standardizovaný toast (jednou)
      if (!silent) processError(error);
      return Promise.reject(error);
    }

    // Všechny ostatní 4xx/5xx (včetně 500) → právě jeden standardizovaný toast
    if (!silent) processError(error);
    return Promise.reject(error);
  }
);

const API_BASE_URL = import.meta.env.VITE_BASE_API || '';

export { apiService, updateToken, refreshAccessToken, ensureCsrfToken, API_BASE_URL };
